import { useEffect, useRef, useState } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import { Badge, Box, DropdownMenu, Flex, Tooltip } from '@radix-ui/themes';
import {
  Check,
  CurrencyDollar,
  Files,
  Gear,
  Handshake,
  HardHat,
  House,
  Key,
  Palette,
  Rocket,
  SidebarSimple,
  UsersThree,
  WebhooksLogo,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import mesaLogo from '@mesa-labs/assets/mesa.svg';
import { sdk, types as api } from '@mesa-labs/mesa-api';
import { useToast } from '@mesa-labs/ui/components/Toast';
import MesaIconSvg from './mesa-icon.svg?react';
import cognitoProvider from '../../../cognito';
import styles from './index.module.css';
import useAuthStore from '../../../store/authStore';
import usePreviewStore from '../../../store/previewStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { useEndPartnerImpersonationMutation, useImpersonatePartnerMutation } from '../../../api/impersonation';
import { ALL_PARTNERS } from '../../../lib/partners';

const MesaIcon = ({ onClick }: { onClick: () => void }) => (
  <button type="button" onClick={onClick} className={styles.mesaIconButton}>
    <MesaIconSvg className={styles.mesaMobileIcon} />
  </button>
);

interface NavItem {
  label: string;
  icon: React.ElementType;
  to?: string;
  href?: string;
  permission?: sdk.AuthPermission;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'Dashboard',
    items: [
      { label: 'Home', icon: House, to: '/', permission: sdk.AuthPermission.InvoicesRead },
      { label: 'Suppliers', icon: HardHat, to: '/suppliers', permission: sdk.AuthPermission.VendorsRead },
      { label: 'Clients', icon: Handshake, to: '/clients', permission: sdk.AuthPermission.ClientsRead },
    ],
  },
  {
    title: 'Account',
    items: [
      { label: 'Revenue', icon: CurrencyDollar, to: '/revenue', permission: sdk.AuthPermission.FinanceRead },
      { label: 'Account Settings', icon: Gear, to: '/account' /* No Permission - Shown always */ },
      { label: 'Team', icon: UsersThree, to: '/team', permission: sdk.AuthPermission.UserAccountsRead },
    ],
  },
  {
    title: 'Developers',
    items: [
      { label: 'Launch Center', icon: Rocket, to: '/launch-center' /* No Permission - Shown only if other Developer sections present */ },
      { label: 'Docs', icon: Files, href: __DOCS_URL__  /* No Permission - Shown only if other Developer sections present */ },
      { label: 'Theme Builder', icon: Palette, to: '/theme-builder', permission: sdk.AuthPermission.ConfigRead },
      { label: 'Webhooks', icon: WebhooksLogo, to: '/webhooks', permission: sdk.AuthPermission.WebhooksRead },
      { label: 'API Keys', icon: Key, to: '/api-keys', permission: sdk.AuthPermission.ApiKeysRead },
    ],
  },
  // {
  //   title: 'Support',
  //   items: [
  //     { label: 'Help Center', icon: Lifebuoy, to: '/help' },
  //   ],
  // },
];

interface NavItemLinkProps {
  item: NavItem;
  collapsed: boolean;
  onCloseMobile?: () => void;
}

function NavItemLink({ item, collapsed, onCloseMobile }: NavItemLinkProps) {
  const match = useMatch({ path: item.to ?? '', end: item.to === '/' });
  const isActive = Boolean(match);
  const Icon = item.icon;

  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(styles.navItem, collapsed && styles.navItemCollapsed)}
        onClick={() => onCloseMobile?.()}
      >
        <Flex align="center" justify={collapsed ? 'center' : 'start'} gap="2">
          <Box flexShrink="0"><Icon color="var(--brand-500, #40396C)" className={styles.navIcon} /></Box>
          {!collapsed && <span>{item.label}</span>}
        </Flex>
      </a>
    );
  }

  return (
    <NavLink
      to={item.to!}
      end={item.to === '/'}
      className={clsx(styles.navItem, collapsed && styles.navItemCollapsed, isActive && styles.navItemActive)}
      onClick={() => onCloseMobile?.()}
    >
      <Flex align="center" justify={collapsed ? 'center' : 'start'} gap="2">
        <Box flexShrink="0"><Icon className={styles.navIcon} /></Box>
        {!collapsed && <span>{item.label}</span>}
      </Flex>
    </NavLink>
  );
}

interface SidebarProps {
  collapsed: boolean;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  onToggle: () => void;
}

function Sidebar({ collapsed, mobileOpen, onCloseMobile, onToggle }: SidebarProps) {
  const email = useAuthStore((s) => s.email);
  const impersonationPartnerId = useAuthStore((s) => s.impersonationPartnerId);
  const setImpersonationPartnerId = useAuthStore((s) => s.setImpersonationPartnerId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const previewMode = usePreviewStore((s) => s.previewMode);
  const { hasPermission, hasDashboardAccess, hasDevelopersAccess } = usePermissions();
  const { toast } = useToast();
  const [animated, setAnimated] = useState(false);
  const resumeTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const onCloseMobileRef = useRef(onCloseMobile);
  onCloseMobileRef.current = onCloseMobile;

  const endPartnerImpersonation = useEndPartnerImpersonationMutation();
  const impersonatePartner = useImpersonatePartnerMutation();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');

    setAnimated(true);

    const handler = () => {
      onCloseMobileRef.current?.();
      setAnimated(false);
      if (resumeTimeout.current) {
        clearTimeout(resumeTimeout.current);
      }
      resumeTimeout.current = setTimeout(() => setAnimated(true), 50);
    };

    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
      if (resumeTimeout.current) {
        clearTimeout(resumeTimeout.current);
      }
    };
  }, []);

  async function handleLogout() {
    if (impersonationPartnerId) {
      await endPartnerImpersonation.mutateAsync(impersonationPartnerId);
    }
    await cognitoProvider.signOut();
    navigate('/sign-in');
    clearAuth();
  }

  async function handleSwitchPartner(partnerId: api.Partners) {
    if (partnerId === impersonationPartnerId) {
      return;
    }

    try {
      await impersonatePartner.mutateAsync(partnerId);
    } catch {
      toast('Failed to switch partner. Please try again.', { variant: 'error' });
      return;
    }

    try {
      await cognitoProvider.refreshCurrentSession();
    } catch {
      // Reload will refresh the session anyway
    }

    setImpersonationPartnerId(partnerId);
    window.location.assign('/');
  }

  return (
    <Flex asChild direction="column" className={clsx(styles.sidebar, collapsed ? styles.sidebarCollapsed : styles.sidebarExpanded, mobileOpen && styles.sidebarMobileOpen, animated && styles.sidebarAnimated)}>
      <aside>
        {!collapsed && impersonationPartnerId && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Badge size="2" className={styles.impersonationBadge}>
                <span className={styles.impersonationBadgeLabel}>
                  Impersonating {api.getPartnerName(impersonationPartnerId)}
                </span>
              </Badge>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start">
              {ALL_PARTNERS.map((p) => (
                <DropdownMenu.Item key={p} onClick={() => handleSwitchPartner(p)}>
                  <Flex align="center" justify="between" gap="4" width="100%">
                    {api.getPartnerName(p)}
                    {p === impersonationPartnerId && <Check size={14} weight="bold" />}
                  </Flex>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}

        <Flex justify="between" align="center" className={clsx(styles.logoContainer, collapsed && styles.logoContainerCollapsed)}>
          {!collapsed && (
            <img src={mesaLogo} alt="Mesa" className={styles.logo} />
          )}

          <div className={styles.collapseToggle}>
            {!collapsed && (
              <SidebarSimple
                color="var(--brand-500, #40396C)"
                size={20}
                onClick={onToggle}
              />
            )}

            {collapsed && (
              <MesaIcon onClick={onToggle} />
            )}
          </div>
        </Flex>

        <Box flexGrow="1" className={styles.scrollArea}>
          <Flex asChild direction="column" gap={collapsed ? "2" : undefined}>
            <nav>
              {navSections.filter(s => {
                if (s.title === 'Dashboard') return hasDashboardAccess;
                if (s.title === 'Developers') return hasDevelopersAccess;

                return true;
              }).map((section, index) => (
                <div key={section.title} className={clsx(styles.section, collapsed && styles.sectionCollapsed)}>
                  {collapsed && index > 0 && <hr className={styles.sectionDivider} />}
                  {!collapsed && (
                    <div className={styles.sectionLabel}>{section.title}</div>
                  )}

                  <Flex direction="column" align={collapsed ? "center" : undefined} gap={collapsed ? "2" : undefined} className={clsx(styles.navItems, collapsed && styles.navItemsCollapsed)}>
                    {section.items.filter(i => {
                      if (i.permission) return hasPermission(i.permission);

                      return true;
                    }).map((item) => {
                      const key = item.href ?? item.to;
                      const linkEl = <NavItemLink key={key} item={item} collapsed={collapsed} onCloseMobile={onCloseMobile} />;

                      if (collapsed) {
                        return (
                          <Tooltip key={key} content={item.label} side="right" delayDuration={0}>
                            {linkEl}
                          </Tooltip>
                        );
                      }

                      return linkEl;
                    })}
                  </Flex>
                </div>
              ))}
            </nav>
          </Flex>
        </Box>

        <Flex justify={collapsed ? 'center' : 'between'} align="center" gap="2" className={styles.sidebarFooter}>
          <Flex justify={collapsed ? 'center' : 'start'} align="center" gap="2" className={styles.sidebarFooter}>
            <Flex align="center" justify="center" className={styles.sidebarAvatar}>{(email?.[0] ?? '?').toUpperCase()}</Flex>
            {!collapsed && <p className={styles.logoutButton} onClick={handleLogout}>Log out</p>}
          </Flex>

          {!collapsed && previewMode && <Badge>Preview</Badge>}
        </Flex>
      </aside>
    </Flex>
  );
}

export default Sidebar;
